"use client";

import { useProductTileClickImpressions } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { Core } from "@faire/design-tokens";
import { Swatch } from "@faire/figma-product-components/shared/components/Swatch";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useLocalNum } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { ProductTileSwatchesProps } from "../../../types";

import { useSwatchInteraction } from "./SwatchInteractionContextProvider";

export const SwatchGroup: React.FC<
  ProductTileSwatchesProps & {
    productToken: string;
    impressionId?: string;
    promotedBy?: Array<keyof typeof IProduct.PromotedType>;
    isPromoted?: boolean;
    brandToken?: string;
  }
> = ({
  optionDetails,
  productToken,
  impressionId,
  promotedBy,
  isPromoted,
  brandToken,
}) => {
  const items = optionDetails?.swatch_details?.swatches_and_options;
  const totalSwatches = optionDetails?.swatch_details?.total_swatch_count ?? 0;
  const viewport = useViewport();
  const { localNum } = useLocalNum();
  const { setSelectedSwatchOptionToken, selectedSwatchOptionToken } =
    useSwatchInteraction();

  const { recordSwatchImageHoverEvent, recordSwatchImageClickEvent } =
    useProductTileClickImpressions(
      {
        token: productToken ?? "",
        impressionId: impressionId ?? "",
        promotedBy: promotedBy,
        isPromoted: isPromoted ?? false,
      },
      brandToken ?? ""
    );

  const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const visibleCount = React.useMemo(() => {
    if (viewport.isXLargeAndAbove) {
      return 6;
    } else if (viewport.isDesktopAndAbove) {
      return 5;
    }
    return 4;
  }, [viewport.isXLargeAndAbove, viewport.isDesktopAndAbove]);

  if (!items) {
    return null;
  }

  const visibleItems = items.slice(0, visibleCount);
  const remainingCount = totalSwatches - visibleCount;

  const onHover = (
    item: ILeanProductTile.IOptionsDetails.ISwatchAndOption,
    index?: number
  ) => {
    if (item.option_token === undefined && item.option_image === undefined) {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setSelectedSwatchOptionToken(item.option_token ?? undefined);
      if (item.option_token) {
        recordSwatchImageHoverEvent(item.option_token, index ?? 0);
      }
    }, 200);
  };

  const onMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  return (
    <Row
      gap={{
        mobileAndAbove: 8,
        desktopAndAbove: 2,
      }}
      align="center"
      className="pt-[4px] pb-[8px]"
      data-test-id="product-tile-swatches"
    >
      {visibleItems.map((item, index) => {
        const hasMissingImage =
          item.option_token === undefined && item.option_image === undefined;
        return (
          <div
            key={item.option_token ?? `swatch-${index}`}
            onMouseEnter={() => onHover(item, index)}
            onMouseLeave={onMouseLeave}
          >
            <Swatch
              key={item.option_token ?? `swatch-${index}`}
              aria-label={`Swatch ${index + 1}`}
              image={item.swatch_image}
              data-test-id={`product-tile-swatch-${item.option_token}`}
              isSmallSize
              onClick={() => {
                if (hasMissingImage) {
                  return;
                }
                setSelectedSwatchOptionToken(item.option_token);
                recordSwatchImageClickEvent(
                  item.option_token ?? "",
                  index ?? 0
                );
              }}
              isChecked={
                selectedSwatchOptionToken !== undefined &&
                selectedSwatchOptionToken === item.option_token
              }
              isDisabled={hasMissingImage}
              name="product-tile-swatches"
            />
          </div>
        );
      })}
      {remainingCount > 0 && (
        <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
          <StrictLocalMsg
            id="sufWzd"
            defaultMessage="+{remainingCountLocalizedNumber}"
            description={{
              text: "This shows a variable number of additional items that are not shown.",
              img: "https://cdn.faire.com/fastly/04013d4e6de3b57c5e00cb4933e117e4d205fafc265cab42bba1572f3a769645.png",
            }}
            values={{
              remainingCountLocalizedNumber: localNum(remainingCount),
            }}
          />
        </Typography>
      )}
    </Row>
  );
};
